import React, { useState, useEffect, useCallback } from 'react';
import { Search, Package, Truck, Clock, Archive, Calendar, ChevronRight, Loader2, Hourglass } from 'lucide-react';
import { JobRow, JobRowHeader } from './JobRow';
import { getPaginatedDeliveries, getHistorySummary } from '../services/api';

const TAB_ITEMS = [
  { key: 'active', label: 'Active', icon: Truck },
  { key: 'expired', label: 'Expired', icon: Hourglass },
  { key: 'recent', label: 'Recent', icon: Clock },
  { key: 'history', label: 'History', icon: Calendar },
  { key: 'archive', label: 'Archive', icon: Archive },
];

export function DeliveryTabs({ children, activeCount = 0, expiredCount = 0, onSelectJob }) {
  const [tab, setTab] = useState('active');
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [historySummary, setHistorySummary] = useState([]);
  const [historyMonth, setHistoryMonth] = useState(null);
  const [tabPage, setTabPage] = useState(1);
  const ITEMS_PER_PAGE = 10;

  const fetchJobs = useCallback(async (tabKey) => {
    setLoading(true);
    try {
      const params = { tab: tabKey, limit: 200 };
      if (search.trim()) params.search = search.trim();
      if (tabKey === 'history' && historyMonth) {
        params.year = historyMonth.year;
        params.month = historyMonth.month;
      }
      const res = await getPaginatedDeliveries(params);
      setJobs(res.data.jobs);
    } catch (e) {
      console.error('Failed to fetch jobs', e);
    } finally {
      setLoading(false);
    }
  }, [search, historyMonth]);

  const fetchHistory = useCallback(async () => {
    try {
      const res = await getHistorySummary();
      setHistorySummary(res.data);
    } catch (e) {}
  }, []);

  useEffect(() => {
    if (tab === 'active') return;
    if (tab === 'history' && !historyMonth) {
      fetchHistory();
      return;
    }
    setJobs([]);
    setTabPage(1);
    fetchJobs(tab);
  }, [tab, historyMonth, fetchJobs, fetchHistory]);

  useEffect(() => {
    if (tab === 'active' || (tab === 'history' && !historyMonth)) return;
    const timer = setTimeout(() => {
      setJobs([]);
      setTabPage(1);
      fetchJobs(tab);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Group history summary by year
  const historyByYear = {};
  historySummary.forEach(item => {
    if (!historyByYear[item.year]) historyByYear[item.year] = [];
    historyByYear[item.year].push(item);
  });

  // Paginate jobs client-side
  const totalPages = Math.max(1, Math.ceil(jobs.length / ITEMS_PER_PAGE));
  const pagedJobs = jobs.slice((tabPage - 1) * ITEMS_PER_PAGE, tabPage * ITEMS_PER_PAGE);

  return (
    <div data-testid="delivery-tabs">
      {/* Tab navigation */}
      <div className="flex gap-1 mb-4 overflow-x-auto pb-1" data-testid="delivery-tab-nav">
        {TAB_ITEMS.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => { setTab(key); setHistoryMonth(null); setSearch(''); }}
            className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md whitespace-nowrap transition-colors
              ${tab === key ? 'bg-[#2E7D32] text-white' : 'text-gray-600 hover:bg-gray-100'}`}
            data-testid={`tab-${key}`}
          >
            <Icon className="w-4 h-4" />
            {label}
            {key === 'active' && activeCount > 0 && (
              <span className="ml-1 bg-white/20 text-[10px] font-bold px-1.5 py-0.5 rounded-full">{activeCount}</span>
            )}
            {key === 'expired' && expiredCount > 0 && (
              <span className={`ml-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full ${tab === key ? 'bg-white/20' : 'bg-red-100 text-red-700'}`} data-testid="tab-expired-count">{expiredCount}</span>
            )}
          </button>
        ))}
      </div>

      {/* Active tab — renders children (existing delivery cards) */}
      {tab === 'active' && (
        <div data-testid="tab-content-active">{children}</div>
      )}

      {/* Recent / Archive / Expired tabs */}
      {(tab === 'recent' || tab === 'archive' || tab === 'expired' || (tab === 'history' && historyMonth)) && (
        <div data-testid={`tab-content-${tab}`}>
          {tab === 'expired' && (
            <div className="mb-3 px-3 py-2 rounded-md bg-red-50 border border-red-100 text-xs text-red-700" data-testid="expired-helper">
              These delivery requests expired without a driver being selected. You can repost any of them anytime — nothing was charged.
            </div>
          )}
          {/* Search */}
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search deliveries..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full border border-gray-200 rounded-md pl-10 pr-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
              data-testid={`${tab}-search`}
            />
          </div>

          {/* Back button for history month */}
          {tab === 'history' && historyMonth && (
            <button onClick={() => setHistoryMonth(null)} className="flex items-center gap-1 text-sm text-[#2E7D32] font-medium mb-3 hover:underline" data-testid="history-back">
              ← Back to months
            </button>
          )}

          {/* Compact job rows */}
          {loading && jobs.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
            </div>
          ) : jobs.length === 0 ? (
            <div className="text-center py-10 text-gray-500 text-sm">
              <Package className="w-8 h-8 text-gray-300 mx-auto mb-2" />
              No deliveries found
            </div>
          ) : (
            <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
              <JobRowHeader />
              {pagedJobs.map(job => (
                <JobRow key={job.id} job={job} priceField="assigned_price" onClick={onSelectJob} />
              ))}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-1 mt-4" data-testid={`${tab}-pagination`}>
              <button
                onClick={() => setTabPage(p => Math.max(1, p - 1))}
                disabled={tabPage === 1}
                className="px-3 py-1.5 text-sm font-medium rounded-md border border-gray-200 disabled:opacity-30 hover:bg-gray-50"
                data-testid={`${tab}-page-prev`}
              >
                Prev
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                <button
                  key={p}
                  onClick={() => setTabPage(p)}
                  className={`w-8 h-8 text-sm font-medium rounded-md ${p === tabPage ? 'bg-[#2E7D32] text-white' : 'border border-gray-200 hover:bg-gray-50'}`}
                  data-testid={`${tab}-page-${p}`}
                >
                  {p}
                </button>
              ))}
              <button
                onClick={() => setTabPage(p => Math.min(totalPages, p + 1))}
                disabled={tabPage === totalPages}
                className="px-3 py-1.5 text-sm font-medium rounded-md border border-gray-200 disabled:opacity-30 hover:bg-gray-50"
                data-testid={`${tab}-page-next`}
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}

      {/* History summary (monthly grouped) */}
      {tab === 'history' && !historyMonth && (
        <div data-testid="tab-content-history-summary">
          {historySummary.length === 0 ? (
            <div className="text-center py-10 text-gray-500 text-sm">
              <Calendar className="w-8 h-8 text-gray-300 mx-auto mb-2" />
              No delivery history yet
            </div>
          ) : (
            <div className="space-y-6">
              {Object.entries(historyByYear).sort((a, b) => b[0] - a[0]).map(([year, months]) => (
                <div key={year}>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{year}</h3>
                  <div className="space-y-1">
                    {months.map(m => (
                      <button
                        key={`${m.year}-${m.month}`}
                        onClick={() => setHistoryMonth({ year: m.year, month: m.month })}
                        className="w-full flex items-center justify-between px-4 py-3 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-gray-300 transition-colors text-left"
                        data-testid={`history-month-${m.year}-${m.month}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center">
                            <Calendar className="w-5 h-5 text-[#2E7D32]" />
                          </div>
                          <div>
                            <p className="font-semibold text-gray-900">{m.month_name}</p>
                            <p className="text-xs text-gray-500">{m.total_jobs} job{m.total_jobs !== 1 ? 's' : ''} • €{m.total_earned.toFixed(2)}</p>
                          </div>
                        </div>
                        <ChevronRight className="w-4 h-4 text-gray-400" />
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
